import type { Bien } from "@/types/bien";
import { todayIso } from "@/lib/today";
import { isForRent } from "@/lib/transaction";

/** Transfert élec considéré "fait" via la date prestataire (migration 048) OU
 *  le boolean legacy. Helper partagé pour aligner badges + statut calculé. */
export function isTransfertElecFait(b: { transfertElecFait?: boolean | string | null; transfertElecDate?: string | null }): boolean {
  if (b.transfertElecDate && typeof b.transfertElecDate === "string" && b.transfertElecDate.trim().length > 0) return true;
  const v = b.transfertElecFait as unknown;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return (v as string).trim().length > 0;
  return false;
}

/**
 * Même logique que `computedStatut` dans SuiviElsDetailPage — partagée pour
 * que la liste, les tiles d'alerte et la page détail soient d'accord sur
 * l'état ELS d'un bien.
 *
 * Le champ `statutEls` en DB est devenu un simple override manuel (rarement
 * utilisé) ; le statut effectif se déduit de `prestataireEle` +
 * `edlDatePrevue` + `transfertElecFait` + `dossierCloture`/`dossierClos`.
 */

export type ComputedStatutEls =
  | "clos"
  | "cloture"
  | "pas_de_prestataire"
  | "transfert_fait"
  | "els_realise"
  | "date_passee"
  | "date_fixee"
  | "avertit";

export function computeStatutEls(b: Bien): ComputedStatutEls {
  if (b.dossierClos) return "clos";
  if (b.dossierCloture) return "cloture";
  if (!b.prestataireEle) return "pas_de_prestataire";
  if (isTransfertElecFait(b)) return "transfert_fait";
  // Priorité absolue à la confirmation manuelle du prestataire : si statutEle
  // === "expertise_ok", l ELS est fait (independamment de la date qui peut
  // etre aujourd hui — la comparaison stricte < today ratait ce cas).
  if (b.statutEle === "expertise_ok") return "els_realise";
  const t = todayIso();
  if (b.edlDatePrevue && b.edlDatePrevue < t) return "date_passee";
  if (b.edlDatePrevue) return "date_fixee";
  return "avertit";
}

/** Un bien est-il dans le périmètre "ELS actif" (location, disponible/en prép).
 *
 *  Spec user 2026-05-23 : un bien clôturé par le collab (dossierCloture=true)
 *  reste visible dans Suivi ELS tant que `appartPretPhotos` n'a pas été tranché
 *  (null/undefined = question photo en attente). Cloture SRS et reponse photo
 *  sont independantes : le dossier peut etre cloture (pastille SRS verte) mais
 *  la fiche reste dans la liste jusqu'a ce que le collab statue Oui/Non sur
 *  les photos. dossierClos (CEO etape 2) reste un exit definitif. */
export function isActiveElsBien(b: Bien): boolean {
  if (!isForRent(b.transaction)) return false;
  if (b.dossierClos) return false;
  if (b.disponibilite !== "EN PRÉPARATION" && b.disponibilite !== "DISPONIBLE") return false;
  const photoPending = b.appartPretPhotos === null || b.appartPretPhotos === undefined;
  return !b.dossierCloture || photoPending;
}
